import { Injectable } from '@angular/core';
import { SessionStorageService } from 'angular-web-storage';

interface SavedDestination {
  path: string;
  savedAt: number;
}

export function validInternalPath(value: unknown): value is string {
  if (typeof value !== 'string' || !value.startsWith('/') || value.startsWith('//')) {
    return false;
  }
  if (
    value.includes('\\') ||
    Array.from(value).some((character) => character.charCodeAt(0) < 32 || character.charCodeAt(0) === 127) ||
    /^\/%2f/i.test(value) ||
    /^\/%5c/i.test(value)
  ) {
    return false;
  }
  try {
    const parsed = new URL(value, window.location.origin);
    return parsed.origin === window.location.origin;
  } catch {
    return false;
  }
}

@Injectable({ providedIn: 'root' })
export class OnboardingReturnService {
  private readonly key = 'onboardingReturnDestination:v1';
  private readonly lifetimeMs = 24 * 60 * 60 * 1000;
  private memoryDestination: SavedDestination | null = null;
  private storageWriteFailed = false;

  constructor(private session: SessionStorageService) {}

  save(path: unknown): void {
    if (!validInternalPath(path)) {
      this.clear();
      return;
    }
    const destination = { path, savedAt: Date.now() };
    this.memoryDestination = destination;
    this.storageWriteFailed = false;
    try {
      this.session.set(this.key, destination);
    } catch {
      this.storageWriteFailed = true;
      // In-memory navigation still works when session storage is unavailable.
    }
  }

  peek(): string | null {
    let destination: SavedDestination | null = null;
    try {
      destination = this.session.get(this.key) as SavedDestination | null;
    } catch {
      destination = this.memoryDestination;
    }
    if (!destination && this.storageWriteFailed) destination = this.memoryDestination;
    if (
      !destination ||
      !validInternalPath(destination.path) ||
      typeof destination.savedAt !== 'number' ||
      destination.savedAt > Date.now() ||
      Date.now() - destination.savedAt > this.lifetimeMs
    ) {
      this.clear();
      return null;
    }
    return destination.path;
  }

  consume(): string {
    const path = this.peek() ?? '/portal';
    this.clear();
    return path;
  }

  isEventDestination(): boolean {
    const path = this.peek();
    return path !== null && /^\/events\/\d+(?:\/[^/?#]+)?$/.test(path);
  }

  clear(): void {
    this.memoryDestination = null;
    this.storageWriteFailed = false;
    try {
      this.session.remove(this.key);
    } catch {
      // A blocked store has no durable destination to remove.
    }
  }
}
